import * as React from 'react';
import _ from 'lodash';
import { Doughnut } from 'react-chartjs-2';
import {
  ACCOUNT_SALES_SHARE,
  ACCOUNT_UNITS_SHARE,
  ITEM_SALE_SHARE,
  ITEM_UNIT_SHARE,
  OPPORTUNITY_SALES_SHARE,
  OPPORTUNITY_VOIDS_SHARE,
  ACCOUNT_SALES_SHARE_PORTFOLIO,
  ACCOUNT_UNITS_SHARE_PORTFOLIO,
  ITEM_SALE_SHARE_PORTFOLIO,
  ITEM_UNIT_SHARE_PORTFOLIO
} from 'apollo/queries';
import {
  ShareAccountSalesType,
  ShareAccountUnitType,
  ShareItemSalesType,
  ShareItemUnitType,
  ShareOpportunitySalesType,
  ShareOpportunityVoidsType
} from 'utils/types';
import { getData, formatTo } from 'utils/functions';
import QueryRetryWrapper from 'components/QueryRetryWrapper';
import { PIE_COLORS } from 'utils/constants';
import './styles.scss';

const mapAmount = data => {
  const amounts = data.map(elem => elem.amount);
  if (amounts.length > 15) {
    return amounts
      .slice(0, 15)
      .concat([
        amounts.slice(15).reduce((accumulator, currentValue) => accumulator + currentValue, 0)
      ]);
  }
  return amounts;
};

const mapLabel = data => {
  const labels = data.map(elem => elem.label);
  if (labels.length > 15) {
    return labels.slice(0, 15).concat(['All Other']);
  }
  return labels;
};

const getPortfolioQuery = (type: string) => {
  switch (type) {
    case ShareAccountSalesType:
      return ACCOUNT_SALES_SHARE_PORTFOLIO;
    case ShareAccountUnitType:
      return ACCOUNT_UNITS_SHARE_PORTFOLIO;
    case ShareItemSalesType:
      return ITEM_SALE_SHARE_PORTFOLIO;
    case ShareItemUnitType:
      return ITEM_UNIT_SHARE_PORTFOLIO;
    default:
      break;
  }
};

const getQuery = (type: string) => {
  switch (type) {
    case ShareAccountSalesType:
      return ACCOUNT_SALES_SHARE;
    case ShareAccountUnitType:
      return ACCOUNT_UNITS_SHARE;
    case ShareItemSalesType:
      return ITEM_SALE_SHARE;
    case ShareItemUnitType:
      return ITEM_UNIT_SHARE;
    case ShareOpportunitySalesType:
      return OPPORTUNITY_SALES_SHARE;
    case ShareOpportunityVoidsType:
      return OPPORTUNITY_VOIDS_SHARE;
    default:
      break;
  }
};

const getDisplayFormat = (type: string) => {
  switch (type) {
    case ShareAccountSalesType:
    case ShareItemSalesType:
    case ShareOpportunitySalesType:
      return 'currency';
    case ShareAccountUnitType:
    case ShareItemUnitType:
    case ShareOpportunityVoidsType:
      return 'number';
    default:
      break;
  }
};

interface Props {
  clientID: string;
  globalFilter: object;
  type: string;
  portfolio?: boolean;
}

const ShareChart: React.FC<Props> = ({ clientID, globalFilter, type, portfolio }) => {
  const displayFormat = getDisplayFormat(type);
  return (
    <QueryRetryWrapper
      query={portfolio ? getPortfolioQuery(type) : getQuery(type)}
      variables={{ clientID, globalFilter }}
      fetchPolicy="cache-first"
    >
      {({ data }) => {
        const sortedData = _.orderBy(getData(data), ['amount'], ['desc']);
        return (
          <div className="doughnut-chart-container">
            <div className="doughnut-chart">
              <Doughnut
                // Height and width are set to null so aspectRatio works properly (https://github.com/jerairrest/react-chartjs-2/issues/362)
                height={null}
                width={null}
                data={{
                  datasets: [
                    {
                      data: mapAmount(sortedData).map(elem => Math.round(elem)),
                      backgroundColor: PIE_COLORS,
                      borderWidth: 0
                    }
                  ]
                }}
                options={{
                  aspectRatio: 1,
                  layout: {
                    padding: {
                      left: 20,
                      right: 20,
                      top: 30,
                      bottom: 30
                    }
                  },
                  legend: {
                    display: false
                  },
                  tooltips: {
                    callbacks: {
                      label: (tooltipItem: { index: number }) => {
                        const label = mapLabel(sortedData)[tooltipItem.index];
                        const amount = formatTo(
                          displayFormat,
                          mapAmount(sortedData)[tooltipItem.index]
                        );
                        return `${label}: ${amount}`;
                      }
                    }
                  }
                }}
              />
            </div>
            <div className="legends">
              {mapLabel(sortedData).map((label, index) => (
                <div className="legend" key={index}>
                  <div className="legend-square" style={{ backgroundColor: PIE_COLORS[index] }} />
                  <div className="label" title={label}>
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }}
    </QueryRetryWrapper>
  );
};

export default ShareChart;
